// Maps each cable circuit's destinations to the plan element they terminate at,
// so the cards can show a little symbol icon + name per destination.
//
// Cable destination endpoints (the LAST waypoint of each path) and element
// `.bbox` are BOTH in PDF-point space, so we match a destination to the nearest
// element by comparing the endpoint to element bbox centres. Matches farther
// than MATCH_MAX_DIST PDF-pts are ignored (no confident element).

import { useMemo } from "react";
import type { CableTakeoffGeometry } from "../utils/fetchCableTakeoffPaths";
import type { GroupedSmartSurveyorElement } from "../../ReviewPage/types";

/** One resolved destination for a circuit path. */
export interface CableDestinationInfo {
  /** Display name (path.destinationName, else the matched element's group label). */
  name: string;
  /** Cropped symbol image for the matched element's group, if any. */
  iconDataUrl?: string;
  /** groupId of the nearest matched element, if within MATCH_MAX_DIST. */
  groupId?: string;
  /**
   * Where this one is, in the drawing's OWN words — the matched element's grid
   * cell, else the text printed beside its symbol. Sixty-two smoke detectors
   * all carry the same group name, so the name cannot tell them apart and the
   * bin icon on each row is unusable without something that can. Undefined when
   * the element carries neither, which is why the caller must not assume it.
   */
  locator?: string;
}

/** Ignore element matches farther than this (PDF-points) from the endpoint. */
export const MATCH_MAX_DIST = 40;

export interface UseCableDestinationInfoArgs {
  geometry: CableTakeoffGeometry | null;
  /**
   * The unedited geometry, when it differs from `geometry`. Destinations the
   * estimator has struck off are filtered out of the derived geometry, so this
   * is the only place their name can still be resolved from.
   */
  baseGeometry?: CableTakeoffGeometry | null;
  byGroupId: Record<string, GroupedSmartSurveyorElement[]>;
  iconByGroupId: ReadonlyMap<string, string>;
}

export interface CableDestinationInfoResult {
  /**
   * Per-circuit list of destination infos (one entry per path/destination), in
   * the same order as `geometry.byCircuit[circuitId].paths`.
   */
  byCircuit: Record<string, CableDestinationInfo[]>;
  /**
   * The same infos keyed by `destinationId`, covering the base geometry too —
   * a device is named by its own identity rather than by the slot it used to
   * occupy. Path order and destination index diverge on their own (the run's
   * destinations with fewer than two coordinates never become paths), so a
   * position is not an identity even before an edit.
   */
  byDestinationId: Record<string, CableDestinationInfo>;
}

/**
 * The grid cell first — it is a position and it is what an estimator reads a
 * plan by. The text printed beside the symbol second. Never composed, never
 * invented: an element carrying neither gets nothing.
 */
const locatorOf = (element: GroupedSmartSurveyorElement): string | undefined => {
  const grid = element.position_description?.trim();
  if (grid) return grid;
  const text = element.txt?.trim();
  return text || undefined;
};

export function useCableDestinationInfo({
  geometry,
  baseGeometry,
  byGroupId,
  iconByGroupId,
}: UseCableDestinationInfoArgs): CableDestinationInfoResult {
  return useMemo(() => {
    const byCircuit: Record<string, CableDestinationInfo[]> = {};
    const byDestinationId: Record<string, CableDestinationInfo> = {};
    if (!geometry && !baseGeometry) return { byCircuit, byDestinationId };

    // Flatten element bbox centres once. The element travels with the centre
    // now: the match already finds it, and everything that could tell two
    // identical symbols apart is on it rather than on its group.
    const centres: Array<{
      groupId: string;
      cx: number;
      cy: number;
      locator?: string;
    }> = [];
    for (const [groupId, elements] of Object.entries(byGroupId)) {
      for (const el of elements) {
        const b = el.bbox;
        if (!b) continue;
        centres.push({
          groupId,
          cx: (b.x1 + b.x2) / 2,
          cy: (b.y1 + b.y2) / 2,
          locator: locatorOf(el),
        });
      }
    }
    const maxD2 = MATCH_MAX_DIST * MATCH_MAX_DIST;

    const resolve = (
      waypoints: Array<{ x: number; y: number }>,
      destinationName: string,
    ): CableDestinationInfo => {
      const last = waypoints[waypoints.length - 1];
      let bestGroupId: string | undefined;
      let bestLocator: string | undefined;
      let bestD2 = maxD2;
      if (last) {
        for (const c of centres) {
          const dx = c.cx - last.x;
          const dy = c.cy - last.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) {
            bestD2 = d2;
            bestGroupId = c.groupId;
            bestLocator = c.locator;
          }
        }
      }
      return {
        // Prefer the matched plan element's group id — that IS the human name
        // shown in the Grundriss-Elemente tab (e.g. "UP Schukosteckdose 1-fach").
        // Fall back to the circuit name only when no element matched.
        name: bestGroupId || destinationName || "",
        groupId: bestGroupId,
        locator: bestLocator,
        iconDataUrl: bestGroupId ? iconByGroupId.get(bestGroupId) : undefined,
      };
    };

    if (geometry) {
      for (const [circuitId, state] of Object.entries(geometry.byCircuit)) {
        byCircuit[circuitId] = state.paths.map((path) => {
          const info = resolve(path.waypoints, path.destinationName);
          byDestinationId[path.destinationId] = info;
          return info;
        });
      }
    }

    // Only the destinations the pass above never saw are matched again — the
    // ones an edit dropped. Naming every struck device costs one match each,
    // not a second match for the whole plan.
    if (baseGeometry && baseGeometry !== geometry) {
      for (const state of Object.values(baseGeometry.byCircuit)) {
        for (const path of state.paths) {
          if (byDestinationId[path.destinationId]) continue;
          byDestinationId[path.destinationId] = resolve(
            path.waypoints,
            path.destinationName,
          );
        }
      }
    }

    return { byCircuit, byDestinationId };
  }, [geometry, baseGeometry, byGroupId, iconByGroupId]);
}
